import React, { useRef, useState } from "react";
import { Briefcase, Send, Upload, CheckCircle2, Users } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "../i18n";
import { PageHero, Reveal, SectionTitle } from "../components/Shared";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ALLOWED_EXT = [".pdf", ".doc", ".docx"];
const MAX_SIZE = 5 * 1024 * 1024;

const INITIAL = { name: "", email: "", phone: "", position: "", message: "" };

const Careers = () => {
  const [form, setForm] = useState(INITIAL);
  const [cvFile, setCvFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const fileInputRef = useRef(null);
  const { t } = useLanguage();
  const tc = t.careersPage;

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setCvFile(null);
      return;
    }
    const ext = "." + file.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      toast.error(tc.cvFormatToast);
      e.target.value = "";
      setCvFile(null);
      return;
    }
    if (file.size > MAX_SIZE) {
      toast.error(tc.cvSizeToast);
      e.target.value = "";
      setCvFile(null);
      return;
    }
    setCvFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.position) {
      toast.error(tc.requiredToast);
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("position", form.position);
      fd.append("message", form.message);
      if (cvFile) fd.append("cv", cvFile);
      await axios.post(`${API}/careers/apply`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(tc.successToast);
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : tc.errorToast);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm(INITIAL);
    setCvFile(null);
    setSent(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div data-testid="careers-page">
      <PageHero
        title={tc.title}
        subtitle={tc.subtitle}
        image="https://images.pexels.com/photos/1181370/pexels-photo-1181370.jpeg?w=1920&q=80"
      />

      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <Reveal>
            <SectionTitle>{tc.sectionTitle}</SectionTitle>
            <p className="text-slate-600 leading-loose font-light">{tc.sectionText}</p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 bg-white">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          {sent ? (
            <Reveal>
              <div className="bg-[#f5f7fb] rounded-lg p-10 text-center" data-testid="careers-confirmation">
                <div className="w-20 h-20 rounded-full bg-[#eef3fc] flex items-center justify-center mx-auto mb-7">
                  <CheckCircle2 size={40} className="text-[#2547a8]" />
                </div>
                <h2 className="text-2xl font-semibold text-[#1b2653] mb-4">{tc.confirmedTitle}</h2>
                <p className="text-slate-600 font-light leading-relaxed mb-8">{tc.confirmedText}</p>
                <button
                  onClick={resetForm}
                  className="inline-flex items-center justify-center rounded-full px-8 py-3.5 text-[13px] font-semibold uppercase tracking-[0.18em] bg-[#1b2653] text-white hover:bg-[#2f6fd8] transition-colors duration-300"
                  data-testid="careers-new-btn"
                >
                  {tc.newApplication}
                </button>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form onSubmit={handleSubmit} className="bg-[#f5f7fb] rounded-lg p-8 md:p-10" data-testid="careers-form">
                <h3 className="flex items-center gap-2.5 text-xl font-semibold text-[#1b2653] mb-7">
                  <Briefcase size={20} className="text-[#2547a8]" />
                  {tc.formTitle}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="car-name" className="text-[#1b2653] text-sm">{tc.name}</Label>
                    <Input id="car-name" name="name" value={form.name} onChange={handleChange} placeholder={tc.namePh} className="mt-2 bg-white h-11" data-testid="careers-input-name" />
                  </div>
                  <div>
                    <Label htmlFor="car-email" className="text-[#1b2653] text-sm">{tc.email}</Label>
                    <Input id="car-email" name="email" type="email" value={form.email} onChange={handleChange} placeholder={tc.emailPh} className="mt-2 bg-white h-11" data-testid="careers-input-email" />
                  </div>
                  <div>
                    <Label htmlFor="car-phone" className="text-[#1b2653] text-sm">{tc.phone}</Label>
                    <Input id="car-phone" name="phone" value={form.phone} onChange={handleChange} placeholder="+224 ..." className="mt-2 bg-white h-11" data-testid="careers-input-phone" />
                  </div>
                  <div>
                    <Label className="text-[#1b2653] text-sm">{tc.position}</Label>
                    <Select value={form.position} onValueChange={(v) => setForm({ ...form, position: v })}>
                      <SelectTrigger className="mt-2 bg-white h-11" data-testid="careers-select-position">
                        <SelectValue placeholder={tc.positionPh} />
                      </SelectTrigger>
                      <SelectContent>
                        {tc.positions.map((p) => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-5">
                  <Label htmlFor="car-message" className="text-[#1b2653] text-sm">{tc.message}</Label>
                  <Textarea id="car-message" name="message" value={form.message} onChange={handleChange} placeholder={tc.messagePh} rows={5} className="mt-2 bg-white resize-none" data-testid="careers-input-message" />
                </div>
                <div className="mt-5">
                  <Label className="text-[#1b2653] text-sm">{tc.cv}</Label>
                  <div className="mt-2 flex items-center gap-4 bg-white border border-slate-200 rounded-md px-4 py-3">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="inline-flex items-center gap-2 rounded-full px-5 py-2 bg-[#eef3fc] text-[#2547a8] text-sm font-medium hover:bg-[#1b2653] hover:text-white transition-colors duration-300 shrink-0"
                      data-testid="careers-cv-btn"
                    >
                      <Upload size={15} />
                      {tc.cvBtn}
                    </button>
                    <span className="text-sm text-slate-500 truncate" data-testid="careers-cv-filename">
                      {cvFile ? cvFile.name : tc.cvNone}
                    </span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFile}
                      className="hidden"
                      data-testid="careers-cv-input"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-7 inline-flex items-center gap-2.5 rounded-full px-9 py-3.5 bg-[#1b2653] text-white text-[13px] font-semibold uppercase tracking-[0.18em] hover:bg-[#2f6fd8] transition-colors duration-300 disabled:opacity-60"
                  data-testid="careers-submit-btn"
                >
                  <Send size={15} />
                  {submitting ? tc.sending : tc.submit}
                </button>
              </form>
            </Reveal>
          )}
        </div>
      </section>

      <section className="bg-[#1b2653] py-16">
        <div className="max-w-4xl mx-auto px-5 text-center">
          <Reveal>
            <Users size={34} className="text-[#6ea8ff] mx-auto mb-4" />
            <p className="text-white/80 font-light max-w-2xl mx-auto">
              contact@inexponens.com — +224 613 47 39 08
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Careers;
